/**
 * KAPE Course Editor — /kcis/admin/university/course-builder/:id
 *
 * Multi-stage wizard:
 *   1. Intake (metadata + generation options)
 *   2. Blueprint (generate + approve)
 *   3. Modules & Lessons (generate lessons, edit, approve)
 *   4. Assessments (quiz + assignment generation, edit, approve)
 *   5. Slides & Narration (generate per lesson)
 *   6. Review & Publish (readiness checklist + publish button + enroll test student)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import KcisAdminShell, { KCIS_ADMIN_PALETTE as PAL } from "./KcisAdminShell";
import {
  useKapeSlidesJob,
  useKapeNarrationScriptJob,
  useKapeNarrationAudioJob,
  useKapeWorkbookJob,
  useKapeFinalExamJob,
  useKapeBulkLessonsJob,
  jobStatusLabel,
} from "./_useKapeJob";
import { NarrationScriptEditor, SlideEditor } from "./KapeEditors";
import PublishingPanel from "./PublishingPanel";
import PreviewAsStudentButton from "./PreviewAsStudentButton";
import CourseAssetsEditor from "./CourseAssetsEditor";
import TeachingTeamPanel from "./TeachingTeamPanel";

const STAGES = [
  { key: "intake",       label: "1 · Course Development Blueprint" },
  { key: "blueprint",    label: "2 · Blueprint Review" },
  { key: "modules",      label: "3 · Modules & Lessons" },
  { key: "assessments",  label: "4 · Assessments" },
  { key: "media",        label: "5 · Slides & Narration" },
  { key: "assets",       label: "6 · Course Assets" },
  { key: "publish",      label: "7 · Review & Publish" },
];

export default function KapeCourseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [stage, setStage] = useState("intake");
  const [busy, setBusy] = useState("");

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/kcis/admin/kape/courses/${id}`);
      setCourse(data.course);
    } catch (e) {
      toast.error("Course not found");
      navigate("/kcis/admin/university/course-builder");
    }
  }, [id, navigate]);

  useEffect(() => { reload(); }, [reload]);

  const withBusy = async (label, fn) => {
    setBusy(label);
    try { await fn(); }
    catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.code || "Operation failed"));
    }
    finally { setBusy(""); }
  };

  if (!course) return <KcisAdminShell><div className="p-8" style={{ color: PAL.muted }}>Loading…</div></KcisAdminShell>;

  return (
    <KcisAdminShell>
      <div className="max-w-6xl mx-auto px-6 py-6" data-testid="kape-editor">
        <div className="flex items-center justify-between">
          <div>
            <Link to="/kcis/admin/university/course-builder" className="text-xs" style={{ color: PAL.accent }}>← Back to courses</Link>
            <div className="mt-1 text-2xl font-semibold">{course.intake?.title || "Untitled Course"}</div>
            <div className="text-xs" style={{ color: PAL.muted }}>
              {course.intake?.course_code || "no code"} · {course.status} · {course.intake?.status_flag}
              {course.kape_engine_version ? ` · ${course.kape_engine_version}` : ""}
              {course.published_at ? ` · published ${course.published_at.slice(0,10)}` : ""}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PreviewAsStudentButton courseId={course.id} testIdSuffix="editor-header" />
            <UpgradeToKapeButton courseId={course.id} reload={reload}
                                    isKape2={course.kape_engine_version === "kape-2.0"}
                                    isAiBuilder={(course.intake?.builder_version || "") === "ai_v1"} />
          </div>
        </div>

        <div className="mt-6 flex gap-2 border-b overflow-x-auto"
             style={{ borderColor: PAL.border }}>
          {STAGES.map((s) => (
            <button key={s.key} onClick={() => setStage(s.key)}
                    className="px-3 py-2 text-sm whitespace-nowrap"
                    data-testid={`kape-stage-${s.key}`}
                    style={{
                      color: stage === s.key ? PAL.text : PAL.muted,
                      borderBottom: stage === s.key ? `2px solid ${PAL.accent}` : "2px solid transparent",
                    }}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {stage === "intake"      && <IntakeStage course={course} reload={reload} withBusy={withBusy} busy={busy} />}
          {stage === "blueprint"   && <BlueprintStage course={course} reload={reload} withBusy={withBusy} busy={busy} />}
          {stage === "modules"     && <ModulesStage course={course} reload={reload} withBusy={withBusy} busy={busy} />}
          {stage === "assessments" && <AssessmentsStage course={course} reload={reload} withBusy={withBusy} busy={busy} />}
          {stage === "media"       && <MediaStage course={course} reload={reload} withBusy={withBusy} busy={busy} />}
          {stage === "assets"      && <CourseAssetsEditor course={course} reload={reload} />}
          {stage === "publish"     && <PublishStage course={course} reload={reload} withBusy={withBusy} busy={busy} />}
        </div>
      </div>
    </KcisAdminShell>
  );
}

// ────────────────────────────────────────────────────────────────
// Stage 1 — Intake
// ────────────────────────────────────────────────────────────────
function IntakeStage({ course, reload, withBusy, busy }) {
  const [intake, setIntake] = useState(course.intake);
  const [profiles, setProfiles] = useState([]);
  useEffect(() => setIntake(course.intake), [course.intake]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/kcis/admin/instructor-profiles");
        setProfiles(data.items || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const setField = (k, v) => setIntake((prev) => ({ ...prev, [k]: v }));

  const save = () =>
    withBusy("save-intake", async () => {
      const { data } = await api.patch(`/kcis/admin/kape/courses/${course.id}`, { intake });
      toast.success("Saved");
      setIntake(data.course.intake);
      await reload();
    });

  const profileOptions = [
    { v: "", l: "— Use platform default —" },
    ...profiles.filter(p => p.active).map(p => ({
      v: p.id,
      l: `${p.identity.full_name}${p.is_default ? " (default)" : ""}`,
    })),
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="kape-stage-intake-panel">
      <div className="lg:col-span-2">
        <TransformationDesignerSection intake={intake} setField={setField}
                                          setIntake={setIntake} onSave={save} />
      </div>
      <div className="lg:col-span-2">
        <TeachingTeamPanel courseId={course.id} intake={intake} />
      </div>

      <Section title="Instructor">
        <SelectField label="Primary instructor" value={intake.primary_instructor_id || ""}
                     options={profileOptions}
                     onChange={(v) => setField("primary_instructor_id", v)}
                     testid="intake-primary-instructor" />
        <div className="text-xs mt-1" style={{ color: PAL.muted }}>
          The selected profile&apos;s voice, pronunciation dictionary, and teaching
          philosophy flow into every LLM prompt and ElevenLabs render.{" "}
          <Link to="/kcis/admin/instructor-profiles" style={{ color: PAL.accent }}>
            Manage instructor profiles →
          </Link>
        </div>
      </Section>

      <Section title="Course identity">
        <Field label="Course title" value={intake.title} onChange={(v) => setField("title", v)} testid="intake-title" />
        <Field label="Course code" value={intake.course_code} onChange={(v) => setField("course_code", v.toUpperCase())} testid="intake-code" />
        <Field label="Subtitle" value={intake.subtitle} onChange={(v) => setField("subtitle", v)} testid="intake-subtitle" />
        <TextArea label="Description" value={intake.description} onChange={(v) => setField("description", v)} rows={3} testid="intake-description" />
        <Field label="Category" value={intake.category} onChange={(v) => setField("category", v)} testid="intake-category" />
        <Field label="Audience" value={intake.audience} onChange={(v) => setField("audience", v)} testid="intake-audience" />
        <SelectField label="Status flag" value={intake.status_flag}
                     options={[{ v: "draft", l: "Draft" }, { v: "internal_test", l: "Internal Test Only" }]}
                     onChange={(v) => setField("status_flag", v)} testid="intake-status-flag" />
      </Section>

      <Section title="Academic structure">
        <NumberField label="Total clock hours" value={intake.total_clock_hours} onChange={(v) => setField("total_clock_hours", v)} testid="intake-clock-hours" />
        <NumberField label="Module count" value={intake.module_count} onChange={(v) => setField("module_count", v)} testid="intake-modules" />
        <NumberField label="Lessons per module" value={intake.lesson_count} onChange={(v) => setField("lesson_count", v)} testid="intake-lessons" />
        <NumberField label="Passing score (%)" value={intake.passing_score} onChange={(v) => setField("passing_score", v)} testid="intake-passing" />
        <ListField label="Learning objectives" value={intake.learning_objectives || []} onChange={(v) => setField("learning_objectives", v)} testid="intake-objectives" />
      </Section>

      <Section title="Teaching direction">
        <Field label="Tone" value={intake.tone} onChange={(v) => setField("tone", v)} testid="intake-tone" />
        <Field label="Reading level" value={intake.reading_level} onChange={(v) => setField("reading_level", v)} testid="intake-reading-level" />
        <Field label="Depth" value={intake.depth} onChange={(v) => setField("depth", v)} testid="intake-depth" />
        <TextArea label="Teaching philosophy" value={intake.teaching_philosophy} onChange={(v) => setField("teaching_philosophy", v)} rows={2} testid="intake-philosophy" />
        <ListField label="Must-include topics" value={intake.must_include_topics || []} onChange={(v) => setField("must_include_topics", v)} testid="intake-must-include" />
        <ListField label="Must-exclude topics" value={intake.must_exclude_topics || []} onChange={(v) => setField("must_exclude_topics", v)} testid="intake-must-exclude" />
      </Section>

      <Section title="Asset selection">
        {[
          ["generate_syllabus", "Syllabus"],
          ["generate_outline", "Course outline"],
          ["generate_lessons", "Full lesson content"],
          ["generate_workbook", "Student workbook"],
          ["generate_assignments", "Assignments"],
          ["generate_quizzes", "Module quizzes"],
          ["generate_final_exam", "Final exam"],
          ["generate_answer_keys", "Answer keys"],
          ["generate_rubrics", "Grading rubrics"],
          ["generate_slides", "Slide content"],
          ["generate_narration", "ElevenLabs narration"],
          ["generate_marketing_copy", "Marketing copy"],
          ["generate_certificate_language", "Certificate language"],
        ].map(([k, l]) => (
          <CheckboxField key={k} label={l} value={!!intake[k]} onChange={(v) => setField(k, v)} testid={`intake-toggle-${k}`} />
        ))}
      </Section>

      <div className="lg:col-span-2 sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={!!busy}
                className="rounded-full px-5 py-2 text-sm font-medium"
                style={{ background: PAL.accent, color: "#0F0B14" }}
                data-testid="kape-intake-save">
          {busy === "save-intake" ? "Saving…" : "Save intake"}
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Stage 2 — Blueprint
// ────────────────────────────────────────────────────────────────
function BlueprintStage({ course, reload, withBusy, busy }) {
  const bp = course.blueprint || {};
  const approved = bp.status === "approved";
  const generate = (overwrite = false) =>
    withBusy("gen-blueprint", async () => {
      await api.post(`/kcis/admin/kape/courses/${course.id}/generate/blueprint`, {
        scope: "blueprint", confirm_overwrite_approved: overwrite,
      });
      toast.success("Blueprint generated + modules seeded");
      await reload();
    });
  const approve = () =>
    withBusy("approve-bp", async () => {
      await api.post(`/kcis/admin/kape/courses/${course.id}/approve`, {
        artefact_kind: "blueprint", artefact_id: "", action: "approve",
      });
      toast.success("Blueprint approved");
      await reload();
    });

  return (
    <div data-testid="kape-stage-blueprint-panel">
      <div className="flex items-center gap-3">
        <button onClick={() => generate(approved)} disabled={!!busy}
                className="rounded-full px-4 py-2 text-sm font-medium"
                style={{ background: PAL.accent, color: "#0F0B14" }}
                data-testid="kape-btn-generate-blueprint">
          {busy === "gen-blueprint" ? "Generating…" : (bp.status ? "Regenerate blueprint" : "Generate blueprint")}
        </button>
        {bp.status && bp.status !== "approved" && (
          <button onClick={approve} disabled={!!busy}
                  className="rounded-full px-4 py-2 text-sm font-medium border"
                  style={{ borderColor: PAL.border, color: PAL.text }}
                  data-testid="kape-btn-approve-blueprint">
            Approve
          </button>
        )}
        {approved && <span className="text-xs px-2 py-0.5 rounded" style={{ background: "#059669", color: "white" }}>Approved</span>}
      </div>

      {!bp.status && (
        <div className="mt-6 text-sm" style={{ color: PAL.muted }}>
          Blueprint has not been generated yet. Click Generate to produce
          the course summary, learning objectives, module structure, and
          certificate language.
        </div>
      )}

      {bp.status && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Section title="Summary">
            <div className="text-sm whitespace-pre-line" style={{ color: PAL.text }}>{bp.summary}</div>
          </Section>
          <Section title="Certificate language">
            <div className="text-sm" style={{ color: PAL.text }}>{bp.certificate_language}</div>
          </Section>
          <Section title="Learning objectives">
            <BulletList items={bp.learning_objectives} />
          </Section>
          <Section title="Course outcomes">
            <BulletList items={bp.course_outcomes} />
          </Section>
          <Section title="Competencies">
            <BulletList items={bp.competencies} />
          </Section>
          <Section title="Assessment plan">
            <div className="text-sm whitespace-pre-line" style={{ color: PAL.text }}>{bp.assessment_plan}</div>
          </Section>
          <div className="md:col-span-2">
            <Section title="Module structure">
              <ol className="text-sm space-y-3">
                {(bp.module_structure || []).map((m, i) => (
                  <li key={i} className="rounded-lg p-3" style={{ background: PAL.bg, border: `1px solid ${PAL.border}` }}>
                    <div className="font-medium">{i + 1}. {m.title} <span className="text-xs" style={{ color: PAL.muted }}>({m.clock_hours}h)</span></div>
                    <div className="text-xs mt-1" style={{ color: PAL.muted }}>{m.overview}</div>
                    <ul className="mt-2 text-xs list-disc pl-5">
                      {(m.lesson_titles || []).map((l, j) => <li key={j} style={{ color: PAL.text }}>{l}</li>)}
                    </ul>
                  </li>
                ))}
              </ol>
            </Section>
          </div>
          <div className="md:col-span-2">
            <Section title="Marketing description">
              <div className="text-sm" style={{ color: PAL.text }}>{bp.marketing_description}</div>
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Stage 3 — Modules & Lessons
// ────────────────────────────────────────────────────────────────
function ModulesStage({ course, reload, withBusy, busy }) {
  const modules = course.modules || [];
  const lessons = course.lessons || [];
  const [openLessonId, setOpenLessonId] = useState(null);

  // Phase 7C Patch 3 — bulk generation is now a durable orchestration job
  // (`bulk_lessons`) that spawns one child `lesson` job per incomplete
  // lesson. The browser tab may close/refresh without cancelling work.
  const bulk = useKapeBulkLessonsJob({
    courseId: course.id,
    onComplete: async (j) => {
      const partial = j?.error_code === "partial_failure";
      if (partial) toast.error(j.error_message_safe || "Some lessons failed — retry individually below");
      else toast.success("All lessons generated");
      await reload();
    },
  });

  const generateAll = async () => {
    if (bulk.isActive) return;
    try {
      await bulk.start("/bulk-lessons");
    } catch { /* error already toasted */ }
  };

  const buttonLabel = bulk.isActive
    ? `Generating (${jobStatusLabel(bulk.job)})`
    : (bulk.isComplete && bulk.job?.error_code === "partial_failure"
        ? "Retry bulk generation"
        : "Generate content for all lessons");

  const childFailures = (bulk.job?.children || []).filter((c) => c.status === "failed" || c.status === "stale");

  return (
    <div data-testid="kape-stage-modules-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={generateAll} disabled={bulk.isActive || !!busy || lessons.length === 0}
                className="rounded-full px-4 py-2 text-sm font-medium"
                style={{ background: PAL.accent, color: "#0F0B14" }}
                data-testid="kape-btn-generate-all-lessons">
          {buttonLabel}
        </button>
        <span className="text-xs" style={{ color: PAL.muted }}>{lessons.length} lesson stub(s) present</span>
        {bulk.isActive && (
          <span className="text-xs" style={{ color: PAL.accent }} data-testid="kape-bulk-progress">
            {jobStatusLabel(bulk.job)} — safe to close this tab; work continues on the server.
          </span>
        )}
        {bulk.isFailed && (
          <button onClick={bulk.retry}
                  className="rounded-full px-3 py-1 text-xs"
                  style={{ background: "#DC2626", color: "white" }}
                  data-testid="kape-btn-retry-bulk">
            Retry bulk generation
          </button>
        )}
      </div>
      {childFailures.length > 0 && (
        <div className="mt-3 rounded-lg p-3 text-xs" style={{ background: "#241014", border: "1px solid #7A1F1F", color: "#FDA4AF" }} data-testid="kape-bulk-failures">
          <div className="font-semibold mb-1">{childFailures.length} lesson(s) failed — retry individually below.</div>
          {childFailures.map((f, i) => (
            <div key={i}>· lesson {f.lesson_id?.slice(0, 8)} — {f.error_code} · ref {f.ref}</div>
          ))}
        </div>
      )}

      <div className="mt-6 space-y-6">
        {modules.map((m) => {
          const ms = m.lesson_ids || [];
          const modLessons = lessons.filter((l) => ms.includes(l.id)).sort((a, b) => a.order - b.order);
          return (
            <Section key={m.id} title={`Module ${m.order} — ${m.title}`}>
              <div className="text-xs mb-3" style={{ color: PAL.muted }}>{m.overview}</div>
              <div className="space-y-2">
                {modLessons.map((l) => (
                  <LessonRow key={l.id} lesson={l} course={course}
                             open={openLessonId === l.id}
                             onToggle={() => setOpenLessonId(openLessonId === l.id ? null : l.id)}
                             reload={reload} withBusy={withBusy} busy={busy} />
                ))}
              </div>
            </Section>
          );
        })}
      </div>
    </div>
  );
}

function LessonRow({ lesson, course, open, onToggle, reload, withBusy, busy }) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(lesson);
  const [job, setJob] = useState(null);
  useEffect(() => setDraft(lesson), [lesson]);

  // Phase 7C Patch 2 — restore in-flight job state on mount/refresh.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/kcis/admin/kape/generation-jobs/for-lesson/${course.id}/${lesson.id}`);
        if (!alive || !data?.job) return;
        if (["queued", "running", "retrying"].includes(data.job.status)) {
          setJob(data.job);
          pollJob(data.job.id);
        } else if (data.job.status === "failed" || data.job.status === "stale") {
          setJob(data.job);
        }
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [course.id, lesson.id]);

  const pollJob = async (jobId) => {
    for (let i = 0; i < 240; i++) {   // up to 20 min at 5s intervals
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const { data } = await api.get(`/kcis/admin/kape/generation-jobs/${jobId}`);
        setJob(data.job);
        if (["completed", "failed", "cancelled", "stale"].includes(data.job.status)) {
          if (data.job.status === "completed") {
            toast.success("Lesson generated");
            await reload();
          } else if (data.job.status === "failed" || data.job.status === "stale") {
            toast.error(`Lesson generation failed — ref ${jobId.slice(0, 8)}`);
          }
          return;
        }
      } catch { /* keep polling */ }
    }
  };

  const startJob = async () => {
    setJob({ status: "queued", current_step: "starting", progress_percent: 0, id: "" });
    try {
      const { data } = await api.post(
        `/kcis/admin/kape/generation-jobs/lesson?course_id=${course.id}&lesson_id=${lesson.id}`
      );
      setJob({ id: data.job_id, status: data.status, current_step: "queued", progress_percent: 0 });
      pollJob(data.job_id);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(status === 409 ? "Lesson is approved — cannot regenerate via async job" : "Could not queue job");
      setJob(null);
    }
  };

  const retryJob = async () => {
    if (!job?.id) return;
    try {
      await api.post(`/kcis/admin/kape/generation-jobs/${job.id}/retry`);
      pollJob(job.id);
    } catch { toast.error("Retry failed"); }
  };

  const regen = () => startJob();
  const approve = (action) =>
    withBusy(`approve-lesson-${lesson.id}`, async () => {
      await api.post(`/kcis/admin/kape/courses/${course.id}/approve`, {
        artefact_kind: "lesson", artefact_id: lesson.id, action,
      });
      toast.success(action === "approve" ? "Lesson approved" : "Lesson rejected");
      await reload();
    });
  const save = () =>
    withBusy(`save-lesson-${lesson.id}`, async () => {
      await api.patch(`/kcis/admin/kape/courses/${course.id}/lesson/${lesson.id}`, { lesson: draft });
      toast.success("Lesson saved");
      setEdit(false);
      await reload();
    });

  return (
    <div className="rounded-lg" style={{ background: PAL.bg, border: `1px solid ${PAL.border}` }}
         data-testid={`kape-lesson-${lesson.id}`}>
      <div className="flex items-center justify-between p-3 cursor-pointer" onClick={onToggle}>
        <div>
          <div className="text-sm font-medium">{lesson.order}. {lesson.title}</div>
          <div className="text-xs" style={{ color: PAL.muted }}>
            {lesson.status} · v{lesson.version} · {lesson.duration_minutes}m
            {lesson.narration?.generated ? " · narration ✓" : ""}
            {job && ["queued","running","retrying"].includes(job.status) && (
              <span className="ml-2" style={{ color: PAL.accent }}>· job: {job.current_step || job.status} {job.progress_percent ? `${job.progress_percent}%` : ""}</span>
            )}
            {job?.status === "failed" && <span className="ml-2" style={{ color: "#F87171" }}>· job failed</span>}
          </div>
        </div>
        <div className="text-xs" style={{ color: PAL.accent }}>{open ? "Collapse" : "Open"}</div>
      </div>
      {open && (
        <div className="border-t p-3" style={{ borderColor: PAL.border }}>
          <div className="flex flex-wrap gap-2 mb-3">
            <button onClick={regen} disabled={job && ["queued","running","retrying"].includes(job.status)}
                    className="rounded-full px-3 py-1 text-xs"
                    style={{ background: PAL.accent, color: "#0F0B14" }}
                    data-testid={`kape-lesson-${lesson.id}-regenerate`}>
              {job && ["queued","running","retrying"].includes(job.status)
                ? `Generating… ${job.progress_percent || 0}%`
                : (lesson.status === "draft" ? "Generate" : "Regenerate")}
            </button>
            {job?.status === "failed" && (
              <button onClick={retryJob}
                      className="rounded-full px-3 py-1 text-xs"
                      style={{ background: "#DC2626", color: "white" }}
                      data-testid={`kape-lesson-${lesson.id}-retry`}>
                Retry generation
              </button>
            )}
            <button onClick={() => setEdit(!edit)}
                    className="rounded-full px-3 py-1 text-xs border" style={{ borderColor: PAL.border, color: PAL.text }}
                    data-testid={`kape-lesson-${lesson.id}-edit-toggle`}>
              {edit ? "Cancel edit" : "Edit"}
            </button>
            {lesson.status !== "approved" && (
              <button onClick={() => approve("approve")}
                      className="rounded-full px-3 py-1 text-xs"
                      style={{ background: "#059669", color: "white" }}
                      data-testid={`kape-lesson-${lesson.id}-approve`}>
                Approve
              </button>
            )}
            {lesson.status === "approved" && <span className="text-xs px-2 py-0.5 rounded" style={{ background: "#059669", color: "white" }}>Approved</span>}
          </div>
          {edit ? (
            <div className="space-y-2">
              <Field label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} testid={`edit-title-${lesson.id}`} />
              <TextArea label="Content (markdown)" value={draft.content_md} onChange={(v) => setDraft({ ...draft, content_md: v })} rows={12} testid={`edit-content-${lesson.id}`} />
              <ListField label="Objectives" value={draft.objectives || []} onChange={(v) => setDraft({ ...draft, objectives: v })} testid={`edit-objectives-${lesson.id}`} />
              <ListField label="Student takeaways" value={draft.student_takeaways || []} onChange={(v) => setDraft({ ...draft, student_takeaways: v })} testid={`edit-takeaways-${lesson.id}`} />
              <TextArea label="Activities" value={draft.activities} onChange={(v) => setDraft({ ...draft, activities: v })} rows={2} testid={`edit-activities-${lesson.id}`} />
              <TextArea label="Reflection prompt" value={draft.reflection_prompt} onChange={(v) => setDraft({ ...draft, reflection_prompt: v })} rows={2} testid={`edit-reflection-${lesson.id}`} />
              <button onClick={save} className="rounded-full px-4 py-1.5 text-xs font-medium"
                      style={{ background: PAL.accent, color: "#0F0B14" }} data-testid={`edit-save-${lesson.id}`}>
                Save lesson
              </button>
            </div>
          ) : (
            <div className="text-sm space-y-3">
              {lesson.objectives?.length > 0 && (
                <div><div className="text-xs uppercase tracking-wider mb-1" style={{ color: PAL.muted }}>Objectives</div>
                  <BulletList items={lesson.objectives} /></div>
              )}
              {lesson.key_terms?.length > 0 && (
                <div><div className="text-xs uppercase tracking-wider mb-1" style={{ color: PAL.muted }}>Key terms</div>
                  <ul className="text-sm pl-5 list-disc">{lesson.key_terms.map((t, i) => <li key={i}><strong>{t.term}</strong> — {t.definition}</li>)}</ul></div>
              )}
              {lesson.content_md && (
                <div><div className="text-xs uppercase tracking-wider mb-1" style={{ color: PAL.muted }}>Content</div>
                  <div className="whitespace-pre-wrap text-sm">{lesson.content_md}</div></div>
              )}
              {lesson.activities && (
                <div><div className="text-xs uppercase tracking-wider mb-1" style={{ color: PAL.muted }}>Activity</div>
                  <div className="text-sm">{lesson.activities}</div></div>
              )}
              {lesson.reflection_prompt && (
                <div><div className="text-xs uppercase tracking-wider mb-1" style={{ color: PAL.muted }}>Reflection</div>
                  <div className="text-sm italic" style={{ color: PAL.muted }}>{lesson.reflection_prompt}</div></div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Stage 4 — Assessments
// ────────────────────────────────────────────────────────────────
function AssessmentsStage({ course, reload, withBusy, busy }) {
  const modules = course.modules || [];
  const quizzes = course.quizzes || [];
  const assignments = course.assignments || [];
  const finalExam = quizzes.find((q) => q.quiz_type === "final_exam");

  // Phase 7C Patch 3 — final exam via durable async job.
  const finalJob = useKapeFinalExamJob({
    courseId: course.id,
    onComplete: async () => { toast.success("Final exam generated"); await reload(); },
  });

  const generateQuiz = (moduleId) =>
    withBusy(`gen-quiz-${moduleId}`, async () => {
      await api.post(`/kcis/admin/kape/courses/${course.id}/generate/quiz/${moduleId}?num_questions=5`);
      toast.success("Quiz generated");
      await reload();
    });
  const generateAssignment = (moduleId) =>
    withBusy(`gen-assn-${moduleId}`, async () => {
      await api.post(`/kcis/admin/kape/courses/${course.id}/generate/assignment/${moduleId}`);
      toast.success("Assignment generated");
      await reload();
    });
  const generateFinal = () => finalJob.start(20);
  const approve = (kind, id) =>
    withBusy(`approve-${kind}-${id}`, async () => {
      await api.post(`/kcis/admin/kape/courses/${course.id}/approve`, {
        artefact_kind: kind, artefact_id: id, action: "approve",
      });
      await reload();
    });

  return (
    <div className="space-y-6" data-testid="kape-stage-assessments-panel">
      {modules.map((m) => {
        const quiz = quizzes.find((q) => q.quiz_type === "module_quiz" && q.module_id === m.id);
        const assn = assignments.find((a) => a.module_id === m.id);
        const wb = m.workbook || {};
        return (
          <Section key={m.id} title={`Module ${m.order} — ${m.title}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Quiz */}
              <ArtefactCard title="Module quiz" status={quiz?.status}
                onGenerate={() => generateQuiz(m.id)} busyKey={`gen-quiz-${m.id}`} busy={busy}
                onApprove={quiz && quiz.status !== "approved" ? () => approve("quiz", quiz.id) : null}
                testid={`kape-mod-${m.id}-quiz`}>
                {quiz?.questions?.length > 0 && (
                  <ol className="text-xs list-decimal pl-4 space-y-1 max-h-40 overflow-y-auto">
                    {quiz.questions.map((q, i) => (
                      <li key={i}>{q.prompt}
                        <ul className="pl-3 text-[10px]" style={{ color: PAL.muted }}>
                          {q.options?.map((o, j) => (
                            <li key={j}>{o.is_correct ? "✓ " : ""}{o.label}</li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ol>
                )}
              </ArtefactCard>

              {/* Assignment */}
              <ArtefactCard title="Assignment" status={assn?.status}
                onGenerate={() => generateAssignment(m.id)} busyKey={`gen-assn-${m.id}`} busy={busy}
                onApprove={assn && assn.status !== "approved" ? () => approve("assignment", assn.id) : null}
                testid={`kape-mod-${m.id}-assn`}>
                {assn?.body_md && <div className="text-xs whitespace-pre-wrap max-h-40 overflow-y-auto">{assn.body_md}</div>}
              </ArtefactCard>

              {/* Workbook — async durable job */}
              <WorkbookCard course={course} module={m} workbook={wb}
                onApprove={wb.status && wb.status !== "approved" ? () => approve("workbook", m.id) : null}
                reload={reload}
                testid={`kape-mod-${m.id}-wb`} />
            </div>
          </Section>
        );
      })}

      <Section title="Final examination (optional)">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <button onClick={generateFinal} disabled={finalJob.isActive}
                  className="rounded-full px-4 py-2 text-sm font-medium"
                  style={{ background: PAL.accent, color: "#0F0B14" }}
                  data-testid="kape-btn-generate-final-exam">
            {finalJob.isActive
              ? `Generating (${jobStatusLabel(finalJob.job)})`
              : (finalExam ? "Regenerate final exam" : "Generate final exam")}
          </button>
          {finalJob.isFailed && (
            <button onClick={finalJob.retry}
                    className="rounded-full px-3 py-1 text-xs"
                    style={{ background: "#DC2626", color: "white" }}
                    data-testid="kape-btn-retry-final-exam">
              Retry
            </button>
          )}
          {finalJob.isFailed && (
            <span className="text-xs" style={{ color: "#F87171" }} data-testid="kape-final-exam-error">
              {finalJob.job?.error_message_safe} · ref {finalJob.job?.id?.slice(0, 8)}
            </span>
          )}
          {finalExam && finalExam.status !== "approved" && (
            <button onClick={() => approve("quiz", finalExam.id)}
                    className="rounded-full px-4 py-2 text-sm border"
                    style={{ borderColor: PAL.border, color: PAL.text }}
                    data-testid="kape-btn-approve-final">
              Approve
            </button>
          )}
        </div>
        {finalJob.isActive && (
          <div className="text-xs mb-3" style={{ color: PAL.accent }} data-testid="kape-final-exam-progress">
            {jobStatusLabel(finalJob.job)} — safe to close this tab; work continues on the server.
          </div>
        )}
        {finalExam?.questions?.length > 0 && (
          <ol className="text-xs list-decimal pl-4 space-y-1 max-h-60 overflow-y-auto">
            {finalExam.questions.map((q, i) => (
              <li key={i}>{q.prompt}</li>
            ))}
          </ol>
        )}
      </Section>
    </div>
  );
}


// Workbook card uses its own durable job hook.
function WorkbookCard({ course, module, workbook, onApprove, reload, testid }) {
  const wbJob = useKapeWorkbookJob({
    courseId: course.id, moduleId: module.id,
    onComplete: async () => { toast.success("Workbook generated"); await reload(); },
  });
  return (
    <div className="rounded-lg p-3" style={{ background: PAL.bg, border: `1px solid ${PAL.border}` }} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium">Workbook</div>
        {workbook?.status && <span className="text-[10px] px-1.5 py-0.5 rounded"
                                    style={{ background: workbook.status === "approved" ? "#059669" : "#4F46E5", color: "white" }}>{workbook.status}</span>}
      </div>
      <div className="space-y-2">
        <button onClick={() => wbJob.start()} disabled={wbJob.isActive}
                className="rounded-full px-3 py-1 text-xs w-full"
                style={{ background: PAL.accent, color: "#0F0B14" }}
                data-testid={`${testid}-generate`}>
          {wbJob.isActive
            ? `Generating (${jobStatusLabel(wbJob.job)})`
            : (workbook?.status ? "Regenerate" : "Generate")}
        </button>
        {wbJob.isFailed && (
          <button onClick={wbJob.retry}
                  className="rounded-full px-3 py-1 text-xs w-full"
                  style={{ background: "#DC2626", color: "white" }}
                  data-testid={`${testid}-retry`}>
            Retry
          </button>
        )}
        {onApprove && (
          <button onClick={onApprove}
                  className="rounded-full px-3 py-1 text-xs w-full"
                  style={{ background: "#059669", color: "white" }}>
            Approve
          </button>
        )}
      </div>
      {wbJob.isFailed && (
        <div className="mt-2 text-xs" style={{ color: "#F87171" }}>
          {wbJob.job?.error_message_safe} · ref {wbJob.job?.id?.slice(0, 8)}
        </div>
      )}
      <div className="mt-3">
        {workbook?.body_md && <div className="text-xs whitespace-pre-wrap max-h-40 overflow-y-auto">{workbook.body_md}</div>}
      </div>
    </div>
  );
}

function ArtefactCard({ title, status, onGenerate, onApprove, children, busyKey, busy, testid }) {
  return (
    <div className="rounded-lg p-3" style={{ background: PAL.bg, border: `1px solid ${PAL.border}` }} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium">{title}</div>
        {status && <span className="text-[10px] px-1.5 py-0.5 rounded"
                         style={{ background: status === "approved" ? "#059669" : "#4F46E5", color: "white" }}>{status}</span>}
      </div>
      <div className="space-y-2">
        <button onClick={onGenerate} disabled={!!busy}
                className="rounded-full px-3 py-1 text-xs w-full"
                style={{ background: PAL.accent, color: "#0F0B14" }}>
          {busy === busyKey ? "Generating…" : (status ? "Regenerate" : "Generate")}
        </button>
        {onApprove && (
          <button onClick={onApprove}
                  className="rounded-full px-3 py-1 text-xs w-full"
                  style={{ background: "#059669", color: "white" }}>
            Approve
          </button>
        )}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Stage 5 — Slides & Narration
// ────────────────────────────────────────────────────────────────
function MediaStage({ course, reload, withBusy, busy }) {
  const lessons = (course.lessons || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  return (
    <div className="space-y-4" data-testid="kape-stage-media-panel">
      {lessons.map((l) => (
        <MediaLessonRow key={l.id} course={course} lesson={l} reload={reload} busy={busy} />
      ))}
    </div>
  );
}

function MediaLessonRow({ course, lesson, reload, busy }) {
  return (
    <Section title={`Lesson ${lesson.order} — ${lesson.title}`}>
      <div className="space-y-4">
        <SlideEditor course={course} lesson={lesson} reload={reload} />
        <NarrationScriptEditor course={course} lesson={lesson} reload={reload} />
        {lesson.narration?.generated && (
          <NarrationPlayer cacheId={lesson.narration.cache_id} lessonId={lesson.id}
                            duration={lesson.narration.duration_seconds} />
        )}
      </div>
    </Section>
  );
}

function NarrationPlayer({ cacheId, lessonId, duration }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      const token = localStorage.getItem("kcis_admin_token");
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/kcis/admin/kape/narration/${cacheId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      if (alive) setUrl(u);
    })();
    return () => { alive = false; };
  }, [cacheId]);
  return (
    <div className="mt-3 rounded-lg p-3" style={{ background: PAL.bg, border: `1px solid ${PAL.border}` }}
         data-testid={`kape-narration-player-${lessonId}`}>
      <div className="text-xs uppercase tracking-wider mb-1" style={{ color: PAL.muted }}>
        Narration audio · ≈{duration}s
      </div>
      {url ? <audio controls src={url} className="w-full" /> : <div className="text-xs" style={{ color: PAL.muted }}>Loading audio…</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Stage 6 — Review & Publish
// ────────────────────────────────────────────────────────────────
function PublishStage({ course, reload, withBusy, busy }) {
  const [readiness, setReadiness] = useState(null);
  const [testEmail, setTestEmail] = useState("square-test+kape@kissedbycvon.com");
  const [enrollment, setEnrollment] = useState(null);

  const loadReadiness = useCallback(async () => {
    const { data } = await api.get(`/kcis/admin/kape/courses/${course.id}/readiness`);
    setReadiness(data);
  }, [course.id]);

  useEffect(() => { loadReadiness(); }, [loadReadiness]);

  const publish = () =>
    withBusy("publish", async () => {
      const { data } = await api.post(`/kcis/admin/kape/courses/${course.id}/publish`, {
        confirm: true, enroll_test_student_email: testEmail,
      });
      toast.success("Course published to LMS");
      if (data.enrollment) setEnrollment(data.enrollment);
      await reload();
      await loadReadiness();
    });

  const enrollOnly = () =>
    withBusy("enroll", async () => {
      const { data } = await api.post(`/kcis/admin/kape/courses/${course.id}/enroll-test-student`, {
        student_email: testEmail, student_name: "Test Student",
      });
      setEnrollment(data.enrollment);
      toast.success("Test student enrolled");
    });

  const approve = (kind, id) =>
    withBusy(`approve-${kind}-${id}`, async () => {
      await api.post(`/kcis/admin/kape/courses/${course.id}/approve`, {
        artefact_kind: "readiness", artefact_id: id, action: "approve",
      });
      await reload();
      await loadReadiness();
    });

  const snap = course.published_snapshot || {};

  return (
    <div className="space-y-6" data-testid="kape-stage-publish-panel">
      <PublishingPanel course={course} onPublished={reload} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Section title="Readiness checklist">
        {!readiness && <div className="text-sm" style={{ color: PAL.muted }}>Loading…</div>}
        {readiness && (
          <ul className="space-y-2 text-sm">
            {readiness.checklist.map((c) => (
              <li key={c.key} className="flex items-center justify-between"
                  data-testid={`readiness-${c.key}`}>
                <span style={{ color: c.checked ? PAL.text : PAL.muted }}>
                  {c.checked ? "✅" : "◻"} {c.label}
                </span>
                {!c.checked && (
                  <button onClick={() => approve("readiness", c.key)}
                          className="text-xs underline" style={{ color: PAL.accent }}
                          data-testid={`readiness-approve-${c.key}`}>
                    Mark complete
                  </button>
                )}
              </li>
            ))}
            <li className="pt-2 mt-2 border-t text-sm" style={{ borderColor: PAL.border, color: readiness.can_publish ? "#10B981" : "#F87171" }}>
              {readiness.can_publish ? "All required items complete — ready to publish." : "Some required items are incomplete."}
            </li>
          </ul>
        )}
      </Section>

      <Section title="Publish & test">
        <div className="text-sm mb-3" style={{ color: PAL.muted }}>
          Publishing writes this course into the KCIS University LMS.
          Modules, lessons, quizzes, and assignments become visible to
          any enrolled student.
        </div>
        <Field label="Test student email" value={testEmail} onChange={setTestEmail} testid="kape-publish-test-email" />
        <button onClick={publish} disabled={!!busy || !readiness?.can_publish}
                className="rounded-full px-5 py-2 text-sm font-medium mt-3"
                style={{ background: readiness?.can_publish ? "#10B981" : PAL.border, color: readiness?.can_publish ? "white" : PAL.muted }}
                data-testid="kape-btn-publish">
          {busy === "publish" ? "Publishing…" : (course.published_at ? "Re-publish" : "Publish to LMS")}
        </button>
        {course.published_at && (
          <button onClick={enrollOnly} disabled={!!busy}
                  className="rounded-full px-5 py-2 text-sm font-medium mt-3 ml-2 border"
                  style={{ borderColor: PAL.border, color: PAL.text }}
                  data-testid="kape-btn-enroll-test">
            {busy === "enroll" ? "Enrolling…" : "Enroll another test student"}
          </button>
        )}

        {course.published_at && (
          <div className="mt-4 rounded-lg p-3 text-xs" style={{ background: PAL.bg, border: `1px solid ${PAL.border}` }}
               data-testid="kape-publish-snapshot">
            <div><strong>Published:</strong> {course.published_at?.slice(0, 19)}</div>
            <div><strong>Course code:</strong> {snap.course_code}</div>
            <div><strong>Program option:</strong> {snap.program_option}</div>
            <div><strong>LMS ids:</strong> {(snap.module_ids?.length || 0)} modules, {(snap.lesson_ids?.length || 0)} lessons, {(snap.quiz_ids?.length || 0)} quizzes</div>
          </div>
        )}
        {(enrollment || course.published_at) && (
          <div className="mt-3 text-xs">
            <div>Test student portal (log in as the student, then use the enrollment):</div>
            <a className="underline" style={{ color: PAL.accent }}
               href={`/university/portal?email=${encodeURIComponent(testEmail)}`}
               data-testid="kape-link-student-portal">
              /university/portal?email={testEmail}
            </a>
            {enrollment && (
              <div className="mt-2">Enrollment id: <code>{enrollment.id}</code></div>
            )}
          </div>
        )}
      </Section>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Small form primitives
// ────────────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div className="rounded-xl p-4"
         style={{ background: PAL.panel, border: `1px solid ${PAL.border}` }}>
      <div className="text-xs uppercase tracking-widest mb-3" style={{ color: PAL.muted }}>{title}</div>
      <div>{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block mb-3">
      <div className="text-xs mb-1" style={{ color: PAL.muted }}>{label}</div>
      <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
             data-testid={testid}
             className="w-full rounded-md px-3 py-2 text-sm"
             style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.text }} />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 3, testid }) {
  return (
    <label className="block mb-3">
      <div className="text-xs mb-1" style={{ color: PAL.muted }}>{label}</div>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows}
                data-testid={testid}
                className="w-full rounded-md px-3 py-2 text-sm font-mono"
                style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.text }} />
    </label>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <label className="block mb-3">
      <div className="text-xs mb-1" style={{ color: PAL.muted }}>{label}</div>
      <input type="number" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))}
             data-testid={testid}
             className="w-full rounded-md px-3 py-2 text-sm"
             style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.text }} />
    </label>
  );
}

function SelectField({ label, value, options, onChange, testid }) {
  return (
    <label className="block mb-3">
      <div className="text-xs mb-1" style={{ color: PAL.muted }}>{label}</div>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)}
              data-testid={testid}
              className="w-full rounded-md px-3 py-2 text-sm"
              style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.text }}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function CheckboxField({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm mb-2">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <span style={{ color: PAL.text }}>{label}</span>
    </label>
  );
}

function ListField({ label, value, onChange, testid }) {
  const [draft, setDraft] = useState("");
  return (
    <div className="mb-3">
      <div className="text-xs mb-1" style={{ color: PAL.muted }}>{label}</div>
      <div className="flex gap-2 mb-2">
        <input type="text" value={draft} onChange={(e) => setDraft(e.target.value)}
               className="flex-1 rounded-md px-3 py-2 text-sm"
               style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.text }}
               data-testid={`${testid}-input`}
               placeholder="Add and press Enter"
               onKeyDown={(e) => {
                 if (e.key === "Enter" && draft.trim()) {
                   e.preventDefault();
                   onChange([...(value || []), draft.trim()]); setDraft("");
                 }
               }} />
        <button type="button"
                onClick={() => { if (draft.trim()) { onChange([...(value || []), draft.trim()]); setDraft(""); } }}
                className="rounded-md px-3 py-2 text-xs border"
                style={{ borderColor: PAL.border, color: PAL.text }}
                data-testid={`${testid}-add`}>Add</button>
      </div>
      <ul className="text-sm space-y-1">
        {(value || []).map((v, i) => (
          <li key={i} className="flex items-center justify-between rounded px-2 py-1"
              style={{ background: PAL.bg, border: `1px solid ${PAL.border}` }}>
            <span>{v}</span>
            <button className="text-xs" style={{ color: "#F87171" }}
                    onClick={() => onChange(value.filter((_, j) => j !== i))}
                    data-testid={`${testid}-remove-${i}`}>Remove</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BulletList({ items }) {
  if (!items || items.length === 0) return <div className="text-xs" style={{ color: PAL.muted }}>—</div>;
  return (
    <ul className="text-sm list-disc pl-5 space-y-1">
      {items.map((x, i) => <li key={i}>{x}</li>)}
    </ul>
  );
}


// ─── KAPE 2.0 — Transformation Designer + Upgrade button ───────────

function TransformationDesignerSection({ intake, setField, setIntake, onSave }) {
  const tf = intake.transformation || {};
  const setTf = (k, v) => setIntake((prev) => ({
    ...prev,
    transformation: { ...(prev.transformation || {}), [k]: v },
  }));
  const outcomes = intake.course_outcomes || [];

  return (
    <div className="rounded-2xl p-5 border-2"
         style={{ borderColor: PAL.accent, background: "#150E1F" }}
         data-testid="kape-transformation-designer">
      <div className="text-[10px] uppercase tracking-widest font-bold"
           style={{ color: PAL.accent }}>
        KAPE 2.0 · Transformation Designer
      </div>
      <div className="text-sm mt-1" style={{ color: PAL.muted }}>
        Define who the learner is today, and who they become after this course.
        Every generated asset optimises toward this transformation — not toward a summary of information.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <label className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
            Who are they today? (starting point)
          </label>
          <textarea value={tf.starting_point || ""}
                    onChange={(e) => setTf("starting_point", e.target.value)}
                    rows={4}
                    placeholder="e.g. A licensed cosmetologist unsure how to evaluate a scalp clinically…"
                    className="w-full rounded p-2 text-sm bg-transparent border mt-1"
                    style={{ borderColor: PAL.border, color: PAL.text }}
                    data-testid="tf-starting-point" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
            Who do they become? (desired endpoint)
          </label>
          <textarea value={tf.desired_endpoint || ""}
                    onChange={(e) => setTf("desired_endpoint", e.target.value)}
                    rows={4}
                    placeholder="e.g. A confident trichology practitioner who can document microscopic findings and communicate a clinical plan…"
                    className="w-full rounded p-2 text-sm bg-transparent border mt-1"
                    style={{ borderColor: PAL.border, color: PAL.text }}
                    data-testid="tf-desired-endpoint" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
        <div>
          <label className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
            Core shift (one line)
          </label>
          <input value={tf.core_shift || ""}
                 onChange={(e) => setTf("core_shift", e.target.value)}
                 placeholder="From cosmetic technician → clinical scalp evaluator"
                 className="w-full rounded p-2 text-sm bg-transparent border mt-1"
                 style={{ borderColor: PAL.border, color: PAL.text }}
                 data-testid="tf-core-shift" />
        </div>
        <ListField label="Signature capabilities they gain"
                    value={tf.signature_capabilities || []}
                    onChange={(v) => setTf("signature_capabilities", v)}
                    testid="tf-capabilities" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
        <ListField label="Confidence markers (observable evidence)"
                    value={tf.confidence_markers || []}
                    onChange={(v) => setTf("confidence_markers", v)}
                    testid="tf-markers" />
        <ListField label="Course-level learning outcomes (measurable)"
                    value={outcomes}
                    onChange={(v) => setField("course_outcomes", v)}
                    testid="tf-outcomes" />
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-[11px]" style={{ color: PAL.muted }}>
          KAPE 2.0 works backward from these outcomes toward the transformation.
        </div>
        <button onClick={onSave}
                className="text-xs rounded-full px-4 py-1.5"
                style={{ background: PAL.accent, color: "#0F0B14" }}
                data-testid="tf-save">
          Save transformation
        </button>
      </div>
    </div>
  );
}


function UpgradeToKapeButton({ courseId, reload, isKape2, isAiBuilder }) {
  const [busy, setBusy] = useState(false);
  // AI Builder courses are already the new engine — never show upgrade.
  if (isAiBuilder) return null;
  if (isKape2) {
    return (
      <span className="text-[10px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full"
            style={{ background: "#1F2A21", color: "#6EE7B7", border: "1px solid #059669" }}
            data-testid="kape-2-badge">
        KAPE 2.0
      </span>
    );
  }
  const run = async () => {
    if (!window.confirm("Upgrade this course to the KAPE 2.0 transformational framework? This scaffolds KISSED framework blocks on every lesson without overwriting any approved content.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/kcis/admin/kape/courses/${courseId}/upgrade-to-kape-2`);
      toast.success(`Upgraded ${data.upgraded_lessons}/${data.total_lessons} lessons to KAPE 2.0`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed");
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={run} disabled={busy}
            className="text-xs rounded-full px-3 py-1 font-semibold disabled:opacity-40"
            style={{ background: PAL.accent, color: "#0F0B14" }}
            data-testid="upgrade-to-kape-2-btn">
      {busy ? "Upgrading…" : "Upgrade to KAPE 2.0"}
    </button>
  );
}
